import React, { useEffect, useMemo, useState } from 'react';
import { Controller, useWatch } from 'react-hook-form';

import formStyles from '../PurchaseOrderForm.module.scss';

import { AutoNumeric } from '../../../../common/auto-numeric/AutoNumeric';
import { CommonProps } from './pricing-list/PricingItem';
import { calculateValue } from '../../../../../utils/helper-functions';
import { PricingCalculator } from '../../../../../utils/PricingCalculator';
import { useUserPermissions } from '../../../../../context/user/PermissionsProvider';
import { PoFormLabel, pricingConfig } from '../po-form-config';
import { FormPricingKeys } from './pricing-list/FormPricing';
import { useAutoPricingState } from './context/AutoPricingProvider';
import { initialAutoPricingState } from '../../../../../constants/constants';

const { min } = pricingConfig[FormPricingKeys.BiWeeklyTerm].validations;

export const biWeeklyTermTestId = 'BiWeeklyTermComponent';

export const BiWeeklyTerm = (props: CommonProps) => {
  const { index, field, state } = props;
  const { hasAutoPricing } = useUserPermissions();
  const { autoPricings } = useAutoPricingState();
  const [value, setValue] = useState('');
  const { biWeeklyTerm: apiValue, weeklyRate, weeklyTerm } =
    autoPricings[state] || initialAutoPricingState;
  const [isDefaultFromPO, setIsDefaultFromPO] = useState(
    field.biWeeklyTerm !== ''
  );

  const weeklyTermWatched = useWatch({ name: `pricing[${index}].weeklyTerm` });
  const weeklyRentWatched = useWatch({ name: `pricing[${index}].weeklyRent` });
  const weeklyWatchedPresent = !!weeklyTermWatched && !!weeklyRentWatched;

  const biWeeklyTerm = () =>
    calculateValue(PricingCalculator.biWeeklyTerm, weeklyTermWatched);

  const fieldName = `pricing[${index}].biWeeklyTerm`;

  useEffect(() => {
    if (
      weeklyTerm === null &&
      weeklyRate === null &&
      apiValue === null &&
      weeklyWatchedPresent &&
      !isDefaultFromPO
    ) {
      //* Value to be calculated from input change
      const rent =
        weeklyWatchedPresent && biWeeklyTerm() >= '1' ? biWeeklyTerm() : '';
      setValue(rent);
    } else {
      setValue('');
      setIsDefaultFromPO(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weeklyTermWatched, weeklyRentWatched]);

  const imperativeValueCheck =
    !!value ||
    !!apiValue ||
    weeklyRentWatched === '' ||
    weeklyTermWatched === '';
  const imperativeValue =
    value !== '' ? value : apiValue ? apiValue.toString() : '';

  return useMemo(
    () => (
      <div data-testid={biWeeklyTermTestId}>
        <label htmlFor={fieldName}>{PoFormLabel.BiWeeklyTerm}:</label>
        <div className={formStyles.noFillField}>
          <Controller
            as={AutoNumeric}
            name={fieldName}
            defaultValue={field.biWeeklyTerm}
            {...(imperativeValueCheck && {
              imperativeValue: imperativeValue?.toString(),
              imperativeMode: true
            })}
            options={{
              minimumValue: 0,
              decimalPlaces: 0,
              ...(!hasAutoPricing && {
                maximumValue: Number.MAX_SAFE_INTEGER
              })
            }}
            placeholder="--"
            disabled
            rules={{ required: true, min }}
          />
        </div>
      </div>
    ),
    [
      fieldName,
      field.biWeeklyTerm,
      imperativeValueCheck,
      imperativeValue,
      hasAutoPricing
    ]
  );
};
